import { useState } from "react";
import { Link } from "react-router-dom";
import { GitBranch, GitCompareArrows, IndianRupee, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useNetWorth, formatINR, shortINR } from "@/context/NetWorthContext";
import { Avatar } from "@/components/PersonCard";
import { NetWorthChart } from "@/components/NetWorthChart";
import { api, errMsg } from "@/lib/api";
import { years } from "@/lib/tree";
import { t } from "@/lib/i18n";
import { toast } from "sonner";

const Row = ({ label, children, testid }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-1 sm:gap-4 py-3 border-b border-[#E5DEC9] last:border-0" data-testid={testid}>
    <dt className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#7E8E85] pt-0.5">{t(label)}</dt>
    <dd className="text-[15px] text-[#1C2421] min-w-0 break-words">{children || <span className="text-[#A9B3AE]">—</span>}</dd>
  </div>
);

const NetWorthForm = ({ p, current, onDone }) => {
  const { user } = useAuth();
  const [amount, setAmount] = useState(current?.amount ?? "");
  const [details, setDetails] = useState(current?.details ?? "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/persons/${p.person_id}/networth`, { amount: Number(amount), details });
      toast.success(data.status === "approved" ? t("Net worth saved") : t("Sent — needs 3 approvals"));
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-2xl bg-[#F4EDE2] p-4 space-y-3" data-testid="networth-form">
      <div className="relative">
        <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7E8E85]" />
        <Input required type="number" min={0} step="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={t("Amount in rupees")} className="pl-9 h-11 rounded-xl bg-white" data-testid="networth-amount-input" />
      </div>
      {amount !== "" && <p className="text-xs text-[#52635B]" data-testid="networth-amount-preview">{formatINR(amount)} · {shortINR(Number(amount))}</p>}
      <Textarea value={details} onChange={(e) => setDetails(e.target.value)} placeholder={t("Details: property, business, savings…")} className="rounded-xl bg-white" data-testid="networth-details-input" />
      <Button type="submit" disabled={busy} className="rounded-full bg-[#1E3A2B] hover:bg-[#162C20]" data-testid="networth-submit-btn">
        {user?.is_admin ? t("Save net worth") : t("Submit for 3 approvals")}
      </Button>
    </form>
  );
};

export const NetWorthRow = ({ p, onSent }) => {
  const { map, visible, reload } = useNetWorth();
  const [editing, setEditing] = useState(false);
  const nw = map[p.person_id];
  if (!visible) return <Row label="Net worth" testid="detail-networth"><span className="flex items-center gap-1.5 text-sm text-[#7E8E85]"><Lock className="w-3.5 h-3.5" />{t("Visible to approved family members")}</span></Row>;
  return (
    <Row label="Net worth" testid="detail-networth">
      {nw ? (
        <div className="space-y-1">
          <p className="font-serif text-xl font-semibold text-[#1E3A2B]" data-testid="detail-networth-amount">{formatINR(nw.amount)} <span className="text-sm font-sans font-normal text-[#7E8E85]">({shortINR(nw.amount)})</span></p>
          {nw.details && <p className="text-sm text-[#52635B] whitespace-pre-line" data-testid="detail-networth-details">{nw.details}</p>}
          <p className="font-mono text-[10px] text-[#A9B3AE]">{t("Updated")} {new Date(nw.updated_at).toLocaleDateString()}</p>
          <NetWorthChart personId={p.person_id} />
        </div>
      ) : <span className="text-[#A9B3AE]">{t("not added")}</span>}
      {editing ? <div className="mt-3"><NetWorthForm p={p} current={nw} onDone={() => { setEditing(false); reload(); onSent?.(); }} /></div> : (
        <button onClick={() => setEditing(true)} className="mt-2 block text-sm text-[#D96B43] font-medium" data-testid="networth-edit-btn">{nw ? t("Suggest an update") : t("Add net worth")}</button>
      )}
    </Row>
  );
};

export const PersonDialog = ({ p, t: tree, onClose, onSelect }) => {
  const { user } = useAuth();
  const parents = p.parent_ids.map((id) => tree.byId[id]).filter(Boolean);
  const father = parents.find((x) => x.gender === "male") || (parents.length === 1 && parents[0].gender !== "female" ? parents[0] : null);
  const mother = parents.find((x) => x.gender === "female");
  const myId = user?.person?.status === "approved" ? user.person.person_id : null;
  const link = (x) => x && <button onClick={() => onSelect(x)} className="text-[#1E3A2B] font-medium hover:text-[#D96B43] underline-offset-4 hover:underline" data-testid={`detail-link-${x.person_id}`}>{x.name}</button>;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="person-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <Avatar p={p} size="w-16 h-16 text-xl" />
            <div>
              <DialogTitle className="font-serif text-2xl" data-testid="detail-name-title">{p.name}</DialogTitle>
              <DialogDescription className="font-mono text-xs">{years(p)}</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <dl>
          <Row label="Name" testid="detail-name">{p.name}</Row>
          <Row label="Father's name" testid="detail-father">{link(father)}</Row>
          {mother && <Row label="Mother's name" testid="detail-mother">{link(mother)}</Row>}
          <Row label="Education" testid="detail-education">{p.education}</Row>
          <Row label="Current occupation" testid="detail-occupation">{p.occupation}</Row>
          <NetWorthRow p={p} />
        </dl>
        {p.bio && <p className="text-sm text-[#52635B] leading-relaxed">{p.bio}</p>}
        <div className="flex flex-wrap gap-2 pt-2">
          <Button asChild className="rounded-full bg-[#1E3A2B] hover:bg-[#162C20]" data-testid="detail-lineage-btn">
            <Link to={`/person/${p.person_id}`} onClick={onClose}><GitBranch className="w-4 h-4 mr-2" />{t("View full lineage")}</Link>
          </Button>
          {myId !== p.person_id && (
            <Button asChild variant="outline" className="rounded-full" data-testid="detail-relation-btn">
              <Link to={`/relations?${myId ? `a=${myId}&` : ""}b=${p.person_id}`} onClick={onClose}><GitCompareArrows className="w-4 h-4 mr-2" />{myId ? t("How am I related?") : t("Find relation")}</Link>
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
